// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import * as AvatarDecorationCommands from '@app/features/user/commands/AvatarDecorationCommands';
import {registerCosmeticPoster} from '@app/features/user/state/CosmeticPosterRegistry';
import {rewriteLegacyApiHostUrl} from '@app/features/user/utils/LegacyApiHostRewrite';
import type {AvatarDecorationCatalogEntryResponse} from '@fluxer/schema/src/domains/user/AvatarDecorationSchemas';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('AvatarDecorationCatalog');

let inflightLoad: Promise<void> | null = null;

class AvatarDecorationCatalog {
	decorations: Array<AvatarDecorationCatalogEntryResponse> = [];
	loaded = false;
	loading = false;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	getById(id: string | null | undefined): AvatarDecorationCatalogEntryResponse | undefined {
		if (!id) {
			return undefined;
		}
		return this.decorations.find((entry) => entry.id === id);
	}

	async load(): Promise<void> {
		if (this.loaded && this.decorations.length > 0) {
			return;
		}
		if (inflightLoad) {
			return inflightLoad;
		}
		inflightLoad = this.fetchCatalog();
		try {
			await inflightLoad;
		} finally {
			inflightLoad = null;
		}
	}

	private async fetchCatalog(): Promise<void> {
		this.loading = true;
		try {
			const response = await AvatarDecorationCommands.fetchAvatarDecorationCatalog();
			runInAction(() => {
				this.decorations = response.avatar_decorations.map((entry) => ({
					...entry,
					image: rewriteLegacyApiHostUrl(entry.image),
					...(entry.poster ? {poster: rewriteLegacyApiHostUrl(entry.poster)} : {}),
				}));
				for (const entry of this.decorations) registerCosmeticPoster(entry.image, entry.poster);
				this.loaded = true;
			});
		} catch (error) {
			logger.warn('Failed to load avatar decoration catalog', error);
			runInAction(() => {
				this.decorations = [];
				this.loaded = true;
			});
		} finally {
			runInAction(() => {
				this.loading = false;
			});
		}
	}
}

export default new AvatarDecorationCatalog();
